/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import { AddVideoProps, Folder, SubCategoryType } from '../../../data/types/content.interface';
import { capitalize, isNull } from 'lodash';
import { AddIcon, CloseIcon, DownIcon, UpIcon } from '../../shared/components/AppIcons';
import LoadingBackdrop from '../../shared/components/LoadingBackdrop';
import { TagType } from '../../../data/types/tags.interface';
import ContentsService from '../../content-creation/services/contents.services';
import theme from '../../../core/theme';
import CategoryOrSubCategoryPopup from './addCategoryOrSubCategoryPopup';
import ThemeService from '../../tags/theme/services/theme.services';

const useStyles = makeStyles({
  paper: {
    minWidth: '800px',
    height: 'auto',
    borderRadius: '4px',
    boxShadow: 'rgba(242, 242, 242, 1)',
  },
  header: {
    fontSize: '28px',
  },
  close: {
    fontSize: '50px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  save: {
    width: '53px',
    height: '40px',
    fontSize: '18px !important',
    boxShadow: 'none !important',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  input: {
    padding: '8px !important',
    width: '250px',
    border: '1px solid #E1E3E8',
  },
  smallText: {
    fontSize: '12px',
    color: theme.palette.outlineInactive.main,
  },
  error: {
    color: '#F57570',
  },
  button: {
    padding: '0',
  },
  textField: {
    '& .MuiInputBase-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
      padding: '4px 8px',
      height: '40px !important',
      fontFamily: 'Lato !important',
    },
    '& .MuiInputBase-input': {
      padding: '6px 0',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
  },
  addCategoryOrSubCategory: {
    fontSize: '35px !important',
    '& path': {
      fill: `#DE5852 !important`,
    },
  },
  radioBtn: {
    '& span': {
      '& svg': {
        fill: '#E1E3E8 !important',
      },
    },
    '& .Mui-checked': {
      '& svg': {
        fill: '#30343F !important',
      },
    },
  },
  outlineInputField: {
    height: '40px',
    // Default border
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

interface Props {
  setOpenAddFolderModal: Dispatch<SetStateAction<boolean>>;
  setRefresh: Dispatch<SetStateAction<boolean>>;
  label: string;
  fileType: string;
  folderNames: Folder[];
  subFolderNames?: Folder[];
  categories?: SubCategoryType[];
  subCategories: SubCategoryType[];
  contentTypes: TagType[];
  setToasterMessage?: Dispatch<SetStateAction<string>>;
}

const AddFolderModal: React.FC<Props> = ({
  setOpenAddFolderModal,
  setRefresh,
  label,
  fileType,
  folderNames,
  subFolderNames,
  categories,
  subCategories,
  contentTypes,
  setToasterMessage,
}) => {
  const classes = useStyles();
  const {
    handleSubmit,
    formState: { errors },
  } = useForm<AddVideoProps>({ mode: 'all' });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [themeList, setThemeList] = useState<TagType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<TagType | null>(null);
  const [videoType, setVideoType] = useState('short');
  const [usedOrUnused, setUsedOrUnused] = useState('NotInUse');
  const [selectedMainFolder, setSelectedMainFolder] = useState<Folder | null>(null);
  const [subFolderList, setSubFolderList] = useState<Folder[]>(subFolderNames || []);
  const [subFolder, setSubFolder] = useState<Folder | null>(null);
  const [theme, setTheme] = useState<TagType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<SubCategoryType | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType | null>(null);
  const [pathLink, setPathLink] = useState<string>('');
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [openCategoryOrSubCategoryModal, setOpenCategoryOrSubCategoryModal] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [folders, setFolders] = useState<Folder[]>([]);
  const [open, setOpen] = useState(false);

  const getAllThemes = async () => {
    const result = await ThemeService.get('theme');
    setThemeList(result);
  };

  useEffect(() => {
    getAllThemes();
  }, []);

  const onSubmit: SubmitHandler<AddVideoProps> = async (form) => {
    setIsFormSubmitted(true);
    try {
      setIsLoading(true);
      const params = {
        themes: theme ? [theme] : [],
        mainFolder: selectedMainFolder?.path_display,
        subFolder: subFolder?.path_display,
        category: selectedCategory,
        subCategory: selectedSubCategory,
        assetType: selectedAsset?.name,
        type: fileType,
        format: videoType || 'short',
        usedOrUnused,
        pathLink: fileType === 'raw' ? subFolder?.path : pathLink,
      };
      await ContentsService.createFolderContent(params);
      setRefresh(true);
      setIsLoading(false);
      setOpenAddFolderModal(false);
      if (setToasterMessage) {
        setToasterMessage('Changes Saved Successfully');
      }
    } catch (error) {
      if (error.status === 304) {
        setIsLoading(false);
        setOpenAddFolderModal(true);
        setErrorMessage('Content with the same path already exists.');
        return;
      }
      setIsLoading(false);
      setOpenAddFolderModal(true);
      setErrorMessage(error?.response?.data[0]?.message || 'Please try again');
    }
  };

  const handleAssetChange = (e: React.SyntheticEvent<Element, Event>, value: TagType | null) => {
    if (isNull(value)) {
      setErrorMessage('Please select any one asset');
    } else {
      setSelectedAsset(value);
      setErrorMessage('');
    }
  };

  const handleVideoTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVideoType((event.target as HTMLInputElement).value);
  };

  const handleUsedOrUnused = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsedOrUnused((event.target as HTMLInputElement).value);
  };

  const handleMainFolderChange = (e: React.SyntheticEvent<Element, Event>, value: any) => {
    if (!isNull(value)) {
      setSelectedMainFolder(value);
      if (subFolderNames) {
        const list =
          subFolderNames.filter((folder) => folder && folder?.path_display.includes(`${value.path_display}/`)) || [];
        setSubFolderList(list);
      }
    }
  };

  const handleSubFolderChange = (e: React.SyntheticEvent<Element, Event>, value: any) => {
    if (!isNull(value)) {
      setSubFolder(value);
    }
  };

  const handlePathChange = (e: React.SyntheticEvent<Element, Event>, value: any) => {
    if (!isNull(value)) {
      setPathLink(value.path);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFolders([]);
    setIsLoading(true);
    try {
      const data = await ContentsService.searchFolders(value, fileType);
      const searchResult =
        data?.filter((f) => {
          if (
            f &&
            subFolder &&
            f?.path?.includes(subFolder?.name) &&
            (((fileType === 'raw' || fileType === 'pre_final') && f.path?.includes('/DWD_HUB')) ||
              ((fileType === 'final') && f.path?.includes('/DWD_Library')))
          ) {
            return f;
          }
        }) || [];
      setFolders(searchResult || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = () => {
    return (
      isNull(selectedAsset) ||
      isNull(selectedMainFolder) ||
      isNull(subFolder) ||
      isNull(theme) ||
      isNull(selectedCategory) ||
      isNull(selectedSubCategory)
    );
  };

  return (
    <Dialog
      open
      aria-modal="true"
      role="dialog"
      onClose={() => setOpenAddFolderModal(false)}
      PaperProps={{
        onClick: (e: any) => e.stopPropagation(),
        className: classes.paper,
      }}
    >
      <DialogTitle>
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Grid2>
            <span className={classes.header}>{label}</span>
          </Grid2>
          <Grid2 container justifyContent="flex-end">
            <IconButton onClick={() => setOpenAddFolderModal(false)} role="button" sx={{ padding: 0 }}>
              <CloseIcon viewBox="0 0 42 42" className={classes.close} />
            </IconButton>
          </Grid2>
        </Grid2>
        <Grid2>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              backgroundColor: '#E1E3E8',
              paddingBottom: '1px',
            }}
          />
        </Grid2>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '0px' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && errorMessage.length > 0 && (
            <Grid2 container justifyContent="center">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>
          )}

          <Grid2 container direction="column" ml={0} mt={3}>
            <label>Asset Types*</label>
            <Stack spacing={1}>
              <Autocomplete
                options={contentTypes || []}
                getOptionLabel={(option) => capitalize(option.name)}
                id="auto-highlight"
                value={selectedAsset}
                autoHighlight
                onChange={handleAssetChange}
                className={`${errors.assetType && 'is-invalid'}`}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                popupIcon={
                  open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                }
                renderInput={(params) => (
                  <TextField {...params} label="" variant="standard" className={classes.textField} />
                )}
              />
            </Stack>
            {isNull(selectedAsset) && isFormSubmitted && (
              <span className={`${classes.smallText} ${classes.error}`}>Asset Type can not be empty</span>
            )}
            {selectedAsset?.name === 'video' && (
              <Grid2 container direction="column" mt={1.5} ml={2} sx={{ width: '48%' }}>
                <>
                  <label>Video Type*</label>
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={videoType}
                      onChange={handleVideoTypeChange}
                    >
                      <Grid2 container>
                        <FormControlLabel
                          value="story"
                          control={<Radio />}
                          className={classes.radioBtn}
                          label={
                            <Tooltip title="Original video clip">
                              <span>Story</span>
                            </Tooltip>
                          }
                        />
                        <FormControlLabel
                          value="long"
                          control={<Radio />}
                          className={classes.radioBtn}
                          label={
                            <Tooltip title="Short videos made out of original clip">
                              <span>Long</span>
                            </Tooltip>
                          }
                        />
                        <FormControlLabel
                          value="short"
                          control={<Radio />}
                          className={classes.radioBtn}
                          label={
                            <Tooltip title="Original short video clip">
                              <span>Short</span>
                            </Tooltip>
                          }
                        />
                      </Grid2>
                    </RadioGroup>
                  </FormControl>
                </>
              </Grid2>
            )}
            <Grid2 container mt={2} direction="column">
              <label>Usage Type*</label>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={usedOrUnused}
                  onChange={handleUsedOrUnused}
                >
                  <Grid2 container direction="row">
                    <FormControlLabel
                      sx={{ ml: '4px' }}
                      className={classes.radioBtn}
                      value="Used"
                      control={<Radio />}
                      label="Used"
                    />
                    <FormControlLabel
                      sx={{ ml: '4px' }}
                      className={classes.radioBtn}
                      value="Unused"
                      control={<Radio />}
                      label="Not Used"
                    />
                    <FormControlLabel
                      sx={{ ml: '4px' }}
                      className={classes.radioBtn}
                      value="NotInUse"
                      control={<Radio />}
                      label="Not In Use"
                    />
                  </Grid2>
                </RadioGroup>
              </FormControl>
            </Grid2>
          </Grid2>

          <Grid2 container mt={1} direction="column">
            <Grid2 mt={2}>
              <label>Theme*</label>{' '}
              <Stack spacing={1}>
                <Autocomplete
                  options={themeList || []}
                  getOptionLabel={(option) => option.name}
                  id="auto-highlight"
                  value={theme}
                  autoHighlight
                  onChange={(e, value) => setTheme(value)}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  popupIcon={
                    open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      variant="standard"
                      placeholder="Select any theme"
                      className={classes.textField}
                    />
                  )}
                />
              </Stack>
              {isNull(theme) && isFormSubmitted && (
                <span className={`${classes.smallText} ${classes.error}`}>Theme can not be empty</span>
              )}
            </Grid2>

            <Grid2 mt={1}>
              <Grid2 container direction="row" justifyContent="space-between">
                <Grid2 mt={2}>
                  <label>Category*</label>{' '}
                </Grid2>
                <Grid2 height="33px">
                  <Button
                    className={classes.button}
                    startIcon={<AddIcon viewBox="0 0 42 42" className={classes.addCategoryOrSubCategory} />}
                    onClick={() => {
                      setAction('addCategory');
                      setOpenCategoryOrSubCategoryModal(true);
                    }}
                  >
                    <span style={{ color: '#DE5852', fontSize: '12px', textTransform: 'none' }}>
                      Create New Category
                    </span>
                  </Button>
                </Grid2>
              </Grid2>
              <Stack spacing={1}>
                <Autocomplete
                  options={categories || []}
                  getOptionLabel={(option) => option.name}
                  id="auto-highlight"
                  value={selectedCategory}
                  autoHighlight
                  onChange={(e, value) => setSelectedCategory(value)}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  popupIcon={
                    open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      variant="standard"
                      placeholder="Select any category"
                      className={classes.textField}
                    />
                  )}
                />
              </Stack>
              {isNull(selectedCategory) && isFormSubmitted && (
                <span className={`${classes.smallText} ${classes.error}`}>Category can not be empty</span>
              )}
            </Grid2>

            <Grid2 mt={1}>
              <Grid2 container direction="row" justifyContent="space-between">
                <Grid2 mt={2}>
                  <label>Sub Category*</label>
                </Grid2>
                <Grid2 height="33px">
                  <Button
                    className={classes.button}
                    startIcon={<AddIcon viewBox="0 0 42 42" className={classes.addCategoryOrSubCategory} />}
                    onClick={() => {
                      setAction('addSubCategory');
                      setOpenCategoryOrSubCategoryModal(true);
                    }}
                  >
                    <span style={{ color: '#DE5852', fontSize: '12px', textTransform: 'none' }}>
                      Create New Sub Category
                    </span>
                  </Button>
                </Grid2>
              </Grid2>
              <Stack spacing={1}>
                <Autocomplete
                  options={subCategories.filter((sc) => sc.themeId === theme?.id) || []}
                  getOptionLabel={(option) => option.name}
                  id="auto-highlight"
                  value={selectedSubCategory}
                  autoHighlight
                  onChange={(e, value) => setSelectedSubCategory(value)}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  popupIcon={
                    open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      variant="standard"
                      placeholder="Select any sub category"
                      className={classes.textField}
                    />
                  )}
                />
              </Stack>
              {isNull(selectedSubCategory) && isFormSubmitted && (
                <span className={`${classes.smallText} ${classes.error}`}>Sub Category can not be empty</span>
              )}
            </Grid2>

            <Grid2 mt={3}>
              <label>Main Folder*</label>
              <Stack spacing={1}>
                <Autocomplete
                  options={(folderNames as Folder[]) || []}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  id="auto-highlight"
                  value={selectedMainFolder}
                  autoHighlight
                  onChange={handleMainFolderChange}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  popupIcon={
                    open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="" variant="standard" className={classes.textField} />
                  )}
                />
              </Stack>
              {isNull(selectedMainFolder) && isFormSubmitted && (
                <span className={`${classes.smallText} ${classes.error}`}>Main Folder can not be empty</span>
              )}
            </Grid2>

            <Grid2 mt={3}>
              <label>Sub Folder*</label>
              <Stack spacing={1}>
                <Autocomplete
                  options={(subFolderList as Folder[]) || []}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  id="auto-highlight"
                  value={subFolder}
                  autoHighlight
                  onChange={handleSubFolderChange}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  popupIcon={
                    open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="" variant="standard" className={classes.textField} />
                  )}
                />
              </Stack>
              {isNull(subFolder) && isFormSubmitted && (
                <span className={`${classes.smallText} ${classes.error}`}>Sub Folder can not be empty</span>
              )}
              <span className={classes.smallText}>{subFolder?.path_display}</span>
            </Grid2>

            {fileType !== 'raw' && (
              <>
                <Grid2 mt={3}>
                  <label>Production Folder*</label>
                </Grid2>
                <span className={classes.smallText}>list all the matched folders based on the search text</span>
                <Grid2>
                  <Autocomplete<Folder, false, false, true>
                    freeSolo
                    options={folders}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                    onInputChange={(e, value) => handleSearch(value)}
                    onChange={handlePathChange}
                    className={classes.outlineInputField}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    popupIcon={open ? <UpIcon className={classes.dropdownIcon} /> : <DownIcon className={classes.dropdownIcon} />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        placeholder="Add Production Folder"
                        sx={{
                          '& .MuiInputBase-root': {
                            border: '0px solid #E1E3E8',
                          },
                        }}
                        className={classes.textField} />
                    )} />
                </Grid2>
                <span className={classes.smallText}>{pathLink}</span>
              </>
            )}
          </Grid2>

          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                marginTop: '32px',
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>

          <Grid2 container justifyContent="end" m={2} mr={0}>
            <Button
              size="small"
              variant="text"
              type="button"
              onClick={() => setOpenAddFolderModal(false)}
              className={classes.cancel}
            >
              cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              className={classes.save}
              disabled={isDisabled()}
              type="submit"
            >
              <div className="caption" style={{ fontSize: 'h5', textTransform: 'none' }}>
                Add
              </div>
            </Button>
          </Grid2>
        </form>
        <LoadingBackdrop openBackdrop={isLoading} />
      </DialogContent>

      {openCategoryOrSubCategoryModal && (
        <CategoryOrSubCategoryPopup
          setOpenPopup={setOpenCategoryOrSubCategoryModal}
          action={action}
          themeId={theme?.id || null}
          setRefresh={setRefresh}
        />
      )}
    </Dialog>
  );
};

export default AddFolderModal;
